import React, { useMemo } from 'react';

// TimeSelect — drop-in replacement for <input type="time">.
//
// Tap-friendly dropdown of 15-minute slots from 06:00 to 23:45 in 24-hour
// format (to match how event times are stored and displayed elsewhere in
// the app). Accepts `value` / `onChange` just like a text input so existing
// code can swap `<input type="time">` for `<TimeSelect>` without changes
// to the handler. `onChange` is called with a synthetic-event-like object
// (`{ target: { name, value } }`) so handlers that do `e.target.name`
// continue to work.
//
// Props:
//   value      — "HH:MM" string (24-hour). Unknown or out-of-range values
//                (e.g. "07:05") are preserved as a one-off extra option so
//                legacy data isn't silently blanked out.
//   onChange   — (e) => void. e.target.value is "HH:MM".
//   name       — optional, forwarded to onChange event.
//   className  — optional, forwarded to the underlying <select>.
//   startHour  — optional, default 6 (06:00).
//   endHour    — optional, default 23 (so last slot is 23:45).
//   stepMinutes— optional, default 15.
//   placeholder — optional label for the blank option (default "--:--").
//   required    — optional, forwarded.
//   disabled    — optional, forwarded.

function pad(n) { return String(n).padStart(2, '0'); }

function buildSlots(startHour, endHour, stepMinutes) {
  const slots = [];
  for (let h = startHour; h <= endHour; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      slots.push(`${pad(h)}:${pad(m)}`);
    }
  }
  return slots;
}

export default function TimeSelect({
  value = '',
  onChange,
  name,
  className = '',
  startHour = 6,
  endHour = 23,
  stepMinutes = 15,
  placeholder = '--:--',
  required = false,
  disabled = false,
  id,
  style,
}) {
  const slots = useMemo(
    () => buildSlots(startHour, endHour, stepMinutes),
    [startHour, endHour, stepMinutes],
  );

  // If the existing value isn't on the standard grid (e.g. "07:05" from
  // legacy data or a restaurant sitting), add it as an extra option so
  // the select can still display it. Otherwise the user would see a blank
  // field and think the time vanished.
  const hasExact = value === '' || slots.includes(value);
  const displaySlots = hasExact ? slots : [value, ...slots];

  const handleChange = (e) => {
    if (typeof onChange !== 'function') return;
    // Forward a plain synthetic event so existing code using
    // `e.target.name` / `e.target.value` keeps working.
    onChange({
      ...e,
      target: { ...e.target, name: name || e.target.name, value: e.target.value },
    });
  };

  return (
    <select
      id={id}
      name={name}
      value={value}
      onChange={handleChange}
      required={required}
      disabled={disabled}
      className={className}
      style={style}
    >
      <option value="">{placeholder}</option>
      {displaySlots.map((t) => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>
  );
}
